import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppDispatch } from "../../../redux/hooks";
import { resolveScopedVaultApi } from "../../../api/vault.api";
import { vaultKeys } from "../queryKeys";
import { setActiveRequirement } from "../../../redux/slices/vaultSlice";
import { setActiveRequirement as setAdminActiveRequirement } from "../../../redux/slices/requirementsSlice";
import { getExceptionMessage } from "../../../exceptions/helpers/getExceptionMessage";
import { vaultExceptions } from "../../../exceptions/default/vaultExceptions";
import {
  cancelPendingUploadBatch,
  confirmUploadSubmissionBestEffort,
  persistUploadBatchForSubmission,
  uploadFilesResumably,
} from "../../../utils/vault/resumableUpload";
import { ResumableUploadError } from "../../../utils/vault/ResumableUploadError";
import {
  getLegacyStaffUploadRecoveryScope,
  getVaultUploadRecoveryContext,
} from "../../../utils/vault/vaultActorSession";
import { withVaultUploadRecoveryLease } from "../../../utils/vault/uploadRecoveryLease";
import {
  DIRECT_MULTIPART_MAX_FILE_BYTES,
  DIRECT_MULTIPART_MAX_TOTAL_BYTES,
  type UploadPolicy,
} from "../../../constants/uploadPolicies";

type UploadTransport = "resumable" | "multipart" | null;

interface UploadFileParams {
  reqId: string;
  formData: FormData;
  uploadPolicy: UploadPolicy;
  skipStatusUpdate?: boolean;
  replaceCurrentEvidence?: boolean;
  uploadBatchAction?: "replace_evidence";
  onResumableFinalized?: (batchId: string) => void;
}

type ApiError = {
  response?: {
    data?: {
      code?: unknown;
      error?: unknown;
      detail?: unknown;
    };
  };
};

const getApiErrorCode = (error: unknown): unknown => {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined;
  }
  return (error as ApiError).response?.data?.code;
};

export function useUploadFileToVault(folderId: string | undefined) {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadTransport, setUploadTransport] = useState<UploadTransport>(null);
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const scopedVaultApi = resolveScopedVaultApi(pathname);

  const mutation = useMutation({
    mutationFn: async ({
      reqId,
      formData,
      uploadPolicy,
      skipStatusUpdate = false,
      replaceCurrentEvidence = false,
      uploadBatchAction,
      onResumableFinalized,
    }: UploadFileParams) => {
      try {
        const { scope: recoveryScope, ownerId } =
          getVaultUploadRecoveryContext();
        const legacyRecoveryScope =
          getLegacyStaffUploadRecoveryScope(recoveryScope);
        return await withVaultUploadRecoveryLease({
          requirementId: reqId,
          recoveryScope,
          ownerId,
          operation: async () => {
            if (legacyRecoveryScope) {
              await withVaultUploadRecoveryLease({
                requirementId: reqId,
                recoveryScope: legacyRecoveryScope,
                ownerId,
                operation: () =>
                  cancelPendingUploadBatch(
                    scopedVaultApi,
                    reqId,
                    undefined,
                    legacyRecoveryScope,
                  ),
              });
            }
            const files = formData
              .getAll("files")
              .filter((value): value is File => value instanceof File);
            const totalBytes = files.reduce(
              (total, file) => total + file.size,
              0,
            );
            const selectedDocumentType = formData.get("document_type");
            const documentType =
              typeof selectedDocumentType === "string"
                ? selectedDocumentType
                : undefined;
            const requiresResumableUpload =
              files.some(
                (file) => file.size > DIRECT_MULTIPART_MAX_FILE_BYTES,
              ) || totalBytes > DIRECT_MULTIPART_MAX_TOTAL_BYTES;

            setUploadProgress(0);
            let uploadBatchId: string | undefined;
            setUploadTransport(
              requiresResumableUpload ? "resumable" : "multipart",
            );
            if (requiresResumableUpload) {
              await uploadFilesResumably({
                api: scopedVaultApi,
                requirementId: reqId,
                files,
                uploadPolicy,
                documentType,
                uploadBatchAction,
                onProgress: setUploadProgress,
                recoveryScope,
                onBatchReady: (batchId) => {
                  uploadBatchId = batchId;
                },
              });
            } else {
              await cancelPendingUploadBatch(
                scopedVaultApi,
                reqId,
                undefined,
                recoveryScope,
              );
              const clientBatchId = crypto.randomUUID();
              formData.set("batch_id", clientBatchId);
              if (uploadBatchAction) {
                formData.set("upload_batch_action", uploadBatchAction);
              }
              persistUploadBatchForSubmission(
                reqId,
                clientBatchId,
                documentType,
                undefined,
                recoveryScope,
              );
              const uploaded = await scopedVaultApi.uploadFile(reqId, formData);
              uploadBatchId = uploaded.batch_id;
              // During a rolling deployment an older backend may ignore the
              // client batch ID. Replace local recovery with the authoritative
              // response as soon as it is available.
              if (uploadBatchId && uploadBatchId !== clientBatchId) {
                persistUploadBatchForSubmission(
                  reqId,
                  uploadBatchId,
                  documentType,
                  undefined,
                  recoveryScope,
                );
              }
              setUploadProgress(100);
            }
            if (!uploadBatchId) {
              throw new ResumableUploadError(
                "UPLOAD_BATCH_REQUIRED",
                "El servidor no devolvió el identificador del lote.",
              );
            }
            onResumableFinalized?.(uploadBatchId);
            if (skipStatusUpdate) {
              return null;
            }
            const updated = replaceCurrentEvidence
              ? await scopedVaultApi.updateRequirementStatus(
                  reqId,
                  "pending_review",
                  uploadBatchId,
                  "replace_evidence_on_submit",
                )
              : await scopedVaultApi.updateRequirementStatus(
                  reqId,
                  "pending_review",
                  uploadBatchId,
                );
            await confirmUploadSubmissionBestEffort(
              scopedVaultApi,
              reqId,
              uploadBatchId,
              undefined,
              recoveryScope,
            );
            return updated;
          },
        });
      } catch (error: unknown) {
        const apiErrorCode = getApiErrorCode(error);
        if (apiErrorCode !== undefined) {
          const responseData = (error as ApiError).response?.data;
          const rawBackendMessage = responseData?.error ?? responseData?.detail;
          const backendMessage =
            typeof rawBackendMessage === "string" &&
            typeof apiErrorCode === "string" &&
            apiErrorCode.startsWith("UPLOAD_")
              ? rawBackendMessage
              : undefined;
          if (backendMessage) {
            throw new Error(backendMessage);
          }
          throw new Error(
            getExceptionMessage(
              apiErrorCode,
              vaultExceptions.uploadFileToVault,
            ),
          );
        }
        if (error instanceof ResumableUploadError) throw error;
        throw new Error(
          getExceptionMessage(undefined, vaultExceptions.uploadFileToVault),
        );
      }
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: vaultKeys.folder(folderId ?? ""),
      });
      if (!data) {
        return;
      }
      dispatch(setActiveRequirement(data));
      dispatch(setAdminActiveRequirement(data));
    },
  });

  return { ...mutation, uploadProgress, uploadTransport };
}
